'use client';

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useScheduler } from '../SchedulerContext';
import { callServerAction } from '@/util/functions/fetching/callServerAction';
import { Quote } from '@/util/types/serverTypes';
import {
  clearDraftFromStorage,
  saveDraftToStorage,
} from '../utils/draftStorage';

export default function DraftLoader() {
  const searchParams = useSearchParams();
  const urlDraftSlug = searchParams.get('draft');
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);
  const isLoadingRef = useRef(false);
  const hasSetUrlFlagRef = useRef(false);

  const {
    setContact,
    setAddress,
    setSelectedPrimaryService,
    setSelectedBundle,
    setSelectedAddons,
    setRequiredInfoValues,
    setEquipmentQuantities,
    setClosingDate,
    setSelectedDateTime,
    setSelectedSlotInspectorInfo,
    draftQuoteSlug,
    setDraftQuoteSlug,
    setDraftQuoteId,
    setIsLoadingDraft,
    setSelectedRole,
    shouldResumeDraft,
    setShouldResumeDraft,
    setSelectedRoleIds,
    jwtToken,
    companyId,
    setAdditionalContacts,
    setWcLeadId,
  } = useScheduler();

  // Priority: URL param > Context state (set by Resume button)
  const draftSlug = urlDraftSlug || draftQuoteSlug;

  // Validate slug format to prevent URL injection attacks
  // Slugs should be UUID format (alphanumeric + hyphens, 20+ chars)
  const isValidSlug = (slug: string | null): boolean => {
    if (!slug) return false;
    // UUID format: 8-4-4-4-12 (e.g., 54185683-8b6f-413a-801e-ba1381241eae)
    // Or MongoDB ObjectId format (24 hex chars)
    const uuidPattern =
      /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
    const objectIdPattern = /^[a-f0-9]{24}$/i;
    return uuidPattern.test(slug) || objectIdPattern.test(slug);
  };

  // Only load if there's a URL param OR the Resume button was clicked
  const shouldLoad =
    (!!urlDraftSlug || shouldResumeDraft) && isValidSlug(draftSlug);

  // If there's a URL param and we haven't set the flag yet, set it
  useEffect(() => {
    if (urlDraftSlug && !hasSetUrlFlagRef.current) {
      setShouldResumeDraft(true);
      hasSetUrlFlagRef.current = true;
    }
  }, [urlDraftSlug, setShouldResumeDraft]);

  // Reset loadedSlug when shouldResumeDraft changes to true (explicit resume action)
  useEffect(() => {
    if (shouldResumeDraft && draftSlug && loadedSlug === draftSlug) {
      setLoadedSlug(null);
    }
  }, [shouldResumeDraft, draftSlug, loadedSlug]);

  useEffect(() => {
    // Track if component is still mounted
    let isMounted = true;
    // AbortController for cancelling in-flight requests
    const abortControllerRef = { current: null as AbortController | null };

    const loadDraft = async () => {
      // DEBUG: Log all conditions at start
      // console.log('[DraftLoader] loadDraft called with:', {
      //   draftSlug,
      //   urlDraftSlug,
      //   draftQuoteSlug,
      //   shouldLoad,
      //   shouldResumeDraft,
      //   loadedSlug,
      //   isLoadingRef: isLoadingRef.current,
      //   isValidSlug: draftSlug ? isValidSlug(draftSlug) : 'no slug',
      // });

      // Only load if:
      // 1. We have a slug
      // 2. Slug is valid format (security check)
      // 3. We should load (URL param or resume button clicked)
      // 4. We haven't already loaded this specific slug (UNLESS shouldResumeDraft is true - force reload)
      // 5. We're not already loading
      if (!draftSlug || !isValidSlug(draftSlug)) {
        // console.log('[DraftLoader] Early return: Invalid or missing slug', {
        //   draftSlug,
        //   isValid: draftSlug ? isValidSlug(draftSlug) : false,
        // });
        if (draftSlug && !isValidSlug(draftSlug)) {
          // Clear invalid slug from context/URL
          if (isMounted) {
            setDraftQuoteSlug(null);
            setShouldResumeDraft(false);
          }
        }
        return;
      }

      const shouldForceReload = shouldResumeDraft && draftSlug;
      if (
        !shouldLoad ||
        (!shouldForceReload && loadedSlug === draftSlug) ||
        isLoadingRef.current
      ) {
        // console.log('[DraftLoader] Early return: Conditions not met', {
        //   shouldLoad,
        //   shouldForceReload,
        //   loadedSlug,
        //   draftSlug,
        //   alreadyLoaded: loadedSlug === draftSlug,
        //   isLoading: isLoadingRef.current,
        // });
        return;
      }

      // console.log('[DraftLoader] ✅ All conditions passed, loading draft...');

      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      // Mark as loading
      isLoadingRef.current = true;
      if (isMounted) {
        setIsLoadingDraft(true);
      }

      try {
        // console.log('[DraftLoader] Fetching quote from API...');
        // Note: AbortSignal cannot be passed to server actions (not serializable)
        // Using isMounted check instead to prevent state updates after unmount
        const quote = await callServerAction<Quote>({
          url: `quote/slug/${draftSlug}`,
          method: 'get',
          params: {
            _companyId: companyId,
          },
          headers: {
            'client-token': jwtToken,
          },
        });

        // console.log('[DraftLoader] API response received:', {
        //   hasQuote: !!quote,
        //   status: quote?.status,
        //   slug: quote?.slug,
        //   hasPeople: !!quote?.people?.length,
        //   hasProperty: !!quote?.property,
        //   hasCharges: !!quote?.charges?.length,
        // });

        // Check if request was aborted
        if (abortControllerRef.current?.signal.aborted) {
          // console.log('[DraftLoader] Request was aborted, skipping');
          return;
        }

        // CRITICAL: Only process results if component is still mounted
        if (!isMounted) {
          // console.log('[DraftLoader] Component unmounted, skipping');
          return;
        }

        if (
          quote &&
          (quote.status === 'draft' || quote.status === 'abandoned')
        ) {
          console.log(
            '[DraftLoader] ✅ Valid quote found (status:',
            quote.status,
            '), beginning hydration...'
          );
          // Valid draft found, load it
          const quoteAny = quote as any; // Cast to any to access potentially missing fields

          // Set draft identifiers
          setDraftQuoteSlug(quote.slug);
          setDraftQuoteId(quote._id);
          
          // Hydrate WhatConverts lead ID if present
          if (quoteAny.wcLeadId) {
            setWcLeadId(quoteAny.wcLeadId);
          }
          // console.log('[DraftLoader] Set draft identifiers:', { slug: quote.slug, id: quote._id });

          // Sync localStorage with loaded draft to ensure ResumeDraftBanner stays hidden
          // Extract contact ID from quote's people array
          const primaryPerson =
            quote.people?.find((p) => p.primary) || quote.people?.[0];
          const contactId = primaryPerson?._contactId
            ? typeof primaryPerson._contactId === 'object'
              ? (primaryPerson._contactId as any)._id
              : primaryPerson._contactId
            : undefined;
          // Save contact ID to localStorage instead of slug
          if (contactId) {
            saveDraftToStorage(contactId);
          }

          // Hydrate Contact - people is an array of objects
          // console.log('[DraftLoader] Hydrating contact from people array:', {
          //   peopleCount: quote.people?.length,
          //   people: quote.people,
          // });
          if (quote.people && quote.people.length > 0) {
            const primaryPerson =
              quote.people.find((p) => p.primary) || quote.people[0];
            // console.log('[DraftLoader] Primary person:', primaryPerson);

            // Type guard for contact
            if (primaryPerson && primaryPerson._contactId) {
              if (
                typeof primaryPerson._contactId === 'object' &&
                primaryPerson._contactId !== null
              ) {
                // console.log('[DraftLoader] ✅ Setting contact:', primaryPerson._contactId);
                setContact(primaryPerson._contactId);
              } else if (typeof primaryPerson._contactId === 'string') {
                // If it's just an ID string, we'd need to fetch the contact
                // For now, skip if it's not a populated object
                // console.log('[DraftLoader] ⚠️ Contact is just an ID string, not populated:', primaryPerson._contactId);
              }
            } else {
              console.log('[DraftLoader] ⚠️ No _contactId on primary person');
            }

            // Type guard for role
            if (primaryPerson && primaryPerson._roleId) {
              if (
                typeof primaryPerson._roleId === 'object' &&
                primaryPerson._roleId !== null &&
                '_id' in primaryPerson._roleId
              ) {
                const roleObj = primaryPerson._roleId as {
                  _id: string | number;
                };
                // console.log('[DraftLoader] ✅ Setting role:', primaryPerson._roleId);
                setSelectedRole(primaryPerson._roleId);
                // Set role IDs for service filtering
                const roleId =
                  typeof roleObj._id === 'string'
                    ? roleObj._id
                    : String(roleObj._id);
                setSelectedRoleIds([roleId]);
              } else {
                console.log(
                  '[DraftLoader] ⚠️ Role is not a populated object:',
                  primaryPerson._roleId
                );
              }
            }

            // Hydrate Additional Contacts (non-primary people)
            const additionalPeople = quote.people.filter((p) => !p.primary);
            if (additionalPeople.length > 0) {
              const additionalContactsArray: any[] = [];

              additionalPeople.forEach((person) => {
                // Both _contactId and _roleId need to be populated objects
                if (
                  person._contactId &&
                  typeof person._contactId === 'object' &&
                  person._contactId !== null &&
                  person._roleId &&
                  typeof person._roleId === 'object' &&
                  person._roleId !== null &&
                  '_id' in person._roleId
                ) {
                  const roleObj = person._roleId as { _id: string | number };
                  const roleId =
                    typeof roleObj._id === 'string'
                      ? roleObj._id
                      : String(roleObj._id);

                  additionalContactsArray.push({
                    _roleId: roleId,
                    _contactId: person._contactId,
                    primary: person.primary || false,
                  });
                }
              });

              if (additionalContactsArray.length > 0) {
                setAdditionalContacts(additionalContactsArray);
              }
            }
          }

          // Hydrate Services - check multiple sources in priority order
          // console.log('[DraftLoader] Hydrating services. Direct fields:', {
          //   selectedPrimaryServiceId: quoteAny.selectedPrimaryServiceId,
          //   selectedBundleId: quoteAny.selectedBundleId,
          //   selectedAddonIds: quoteAny.selectedAddonIds,
          //   chargesCount: quote.charges?.length,
          // });
          // 1. First try direct quote fields (if they exist - they're not in the schema but might be in some quotes)
          if (
            quoteAny.selectedPrimaryServiceId &&
            typeof quoteAny.selectedPrimaryServiceId === 'string'
          ) {
            // console.log('[DraftLoader] ✅ Setting primary service from direct field:', quoteAny.selectedPrimaryServiceId);
            setSelectedPrimaryService(quoteAny.selectedPrimaryServiceId);
          }
          if (
            quoteAny.selectedBundleId &&
            typeof quoteAny.selectedBundleId === 'string'
          ) {
            // console.log('[DraftLoader] ✅ Setting bundle from direct field:', quoteAny.selectedBundleId);
            setSelectedBundle(quoteAny.selectedBundleId);
          }
          if (
            Array.isArray(quoteAny.selectedAddonIds) &&
            quoteAny.selectedAddonIds.length > 0
          ) {
            const validAddonIds = quoteAny.selectedAddonIds.filter(
              (id: any) => typeof id === 'string'
            );
            if (validAddonIds.length > 0) {
              // console.log('[DraftLoader] ✅ Setting addons from direct field:', validAddonIds);
              setSelectedAddons(validAddonIds);
            }
          }

          // 2. If no primary service from quote fields, try extracting from charges
          if (
            !quoteAny.selectedPrimaryServiceId &&
            quote.charges &&
            Array.isArray(quote.charges) &&
            quote.charges.length > 0
          ) {
            // console.log('[DraftLoader] Extracting services from charges array:', quote.charges);
            const charges = quote.charges as any[];

            // Find primary charge (has isPrimary flag)
            const primaryCharge = charges.find(
              (charge: any) => charge.isPrimary === true
            );
            // console.log('[DraftLoader] Primary charge:', primaryCharge);

            if (primaryCharge && primaryCharge._serviceId) {
              // Extract service ID - could be object or string
              const serviceId =
                typeof primaryCharge._serviceId === 'object' &&
                primaryCharge._serviceId !== null
                  ? primaryCharge._serviceId._id || primaryCharge._serviceId
                  : primaryCharge._serviceId;

              if (serviceId && typeof serviceId === 'string') {
                // console.log('[DraftLoader] ✅ Setting primary service from charges:', serviceId);
                setSelectedPrimaryService(serviceId);
              }
            } else if (charges.length > 0 && charges[0]._serviceId) {
              // Fallback: use first charge if no primary marked
              const serviceId =
                typeof charges[0]._serviceId === 'object' &&
                charges[0]._serviceId !== null
                  ? charges[0]._serviceId._id || charges[0]._serviceId
                  : charges[0]._serviceId;

              if (serviceId && typeof serviceId === 'string') {
                // console.log('[DraftLoader] ✅ Setting primary service from first charge:', serviceId);
                setSelectedPrimaryService(serviceId);
              }
            } else {
              console.log(
                '[DraftLoader] ⚠️ No primary service found in charges'
              );
            }

            // Extract bundle from charges
            const bundleCharge = charges.find(
              (charge: any) => charge._bundleId
            );
            if (
              bundleCharge &&
              bundleCharge._bundleId &&
              !quoteAny.selectedBundleId
            ) {
              const bundleId =
                typeof bundleCharge._bundleId === 'object' &&
                bundleCharge._bundleId !== null
                  ? bundleCharge._bundleId._id || bundleCharge._bundleId
                  : bundleCharge._bundleId;

              if (bundleId && typeof bundleId === 'string') {
                setSelectedBundle(bundleId);
              }
            }

            // Extract addons from charges (non-primary charges)
            const primaryServiceId =
              primaryCharge && primaryCharge._serviceId
                ? typeof primaryCharge._serviceId === 'object' &&
                  primaryCharge._serviceId !== null
                  ? primaryCharge._serviceId._id || primaryCharge._serviceId
                  : primaryCharge._serviceId
                : null;

            const addonCharges = charges.filter((charge: any) => {
              if (charge.isPrimary === true) return false;
              if (!charge._serviceId) return false;

              const chargeServiceId =
                typeof charge._serviceId === 'object' &&
                charge._serviceId !== null
                  ? charge._serviceId._id || charge._serviceId
                  : charge._serviceId;

              // Exclude if it's the same as primary service
              return primaryServiceId
                ? String(chargeServiceId) !== String(primaryServiceId)
                : true;
            });

            if (
              addonCharges.length > 0 &&
              (!quoteAny.selectedAddonIds ||
                quoteAny.selectedAddonIds.length === 0)
            ) {
              const addonIds = addonCharges
                .map((charge: any) => {
                  const serviceId =
                    typeof charge._serviceId === 'object' &&
                    charge._serviceId !== null
                      ? charge._serviceId._id || charge._serviceId
                      : charge._serviceId;
                  return serviceId && typeof serviceId === 'string'
                    ? serviceId
                    : null;
                })
                .filter((id: string | null): id is string => id !== null);

              if (addonIds.length > 0) {
                setSelectedAddons(addonIds);
              }
            }
          }

          // 3. Fallback: Also check message field (legacy format)
          if (!quoteAny.selectedPrimaryServiceId && quote.message) {
            try {
              // Validate message is a string before parsing
              if (
                typeof quote.message === 'string' &&
                quote.message.length <= 10000
              ) {
                const parsedMessage = JSON.parse(quote.message);

                // Validate structure before using
                if (
                  parsedMessage &&
                  typeof parsedMessage === 'object' &&
                  parsedMessage.draftServices
                ) {
                  const {
                    selectedPrimaryServiceId,
                    selectedBundleId,
                    selectedAddonIds,
                  } = parsedMessage.draftServices;

                  // Only set if not already set from quote fields or charges
                  if (
                    selectedPrimaryServiceId &&
                    typeof selectedPrimaryServiceId === 'string' &&
                    !quoteAny.selectedPrimaryServiceId
                  ) {
                    setSelectedPrimaryService(selectedPrimaryServiceId);
                  }
                  if (
                    selectedBundleId &&
                    typeof selectedBundleId === 'string' &&
                    !quoteAny.selectedBundleId
                  ) {
                    setSelectedBundle(selectedBundleId);
                  }
                  if (
                    Array.isArray(selectedAddonIds) &&
                    selectedAddonIds.length > 0 &&
                    (!quoteAny.selectedAddonIds ||
                      quoteAny.selectedAddonIds.length === 0)
                  ) {
                    const validAddonIds = selectedAddonIds.filter(
                      (id) => typeof id === 'string'
                    );
                    if (validAddonIds.length > 0) {
                      setSelectedAddons(validAddonIds);
                    }
                  }
                }
              }
            } catch (e) {
              // Don't throw - continue loading other draft data
            }
          }

          // Hydrate Property
          // console.log('[DraftLoader] Hydrating property:', quote.property);
          if (quote.property) {
            // Validate property is an object with expected structure
            if (typeof quote.property === 'object' && quote.property !== null) {
              // Ensure it has at least some expected properties
              const property = quote.property as Record<string, any>;
              if (
                property.street ||
                property.address ||
                property.lat ||
                property.lng
              ) {
                // console.log('[DraftLoader] ✅ Setting address:', property);
                // Type assertion: we've validated the structure, now cast to AddressForScheduler
                setAddress(property as any); // AddressForScheduler type
              } else {
                console.log(
                  '[DraftLoader] ⚠️ Property missing required fields (street/address/lat/lng)'
                );
              }
            }
          } else {
            console.log('[DraftLoader] No property on quote');
          }

          // Hydrate Required Info & Equipment
          if (quote.requiredInfoValues) {
            // Validate it's an array
            if (Array.isArray(quote.requiredInfoValues)) {
              // Filter out invalid entries and ensure required fields exist
              const validRequiredInfo = quote.requiredInfoValues.filter(
                (item: any) => item && typeof item._requiredInfoId === 'string'
              ) as any[];
              if (validRequiredInfo.length > 0) {
                setRequiredInfoValues(validRequiredInfo);
              }
            }
          }
          if (quote.equipmentQuantities) {
            // Validate it's an array. Preserve manualChange when API returns it; otherwise set manualChange: true so merge doesn't overwrite saved values.
            if (Array.isArray(quote.equipmentQuantities)) {
              setEquipmentQuantities(
                quote.equipmentQuantities.map((eq: { _equipmentCategoryId: string; value: number; manualChange?: boolean }) => ({
                  ...eq,
                  manualChange: eq.manualChange ?? true,
                }))
              );
            }
          }

          // A closing date the customer entered before abandoning the draft
          if (typeof quote.closingDate === 'string') {
            setClosingDate(quote.closingDate);
          }

          // Hydrate Schedule
          if (quote.holdDatetime) {
            // Validate holdDatetime is a string or Date
            const holdDateTimeValue = quote.holdDatetime as any;
            if (
              typeof holdDateTimeValue === 'string' ||
              (holdDateTimeValue &&
                typeof holdDateTimeValue === 'object' &&
                'getTime' in holdDateTimeValue)
            ) {
              setSelectedDateTime(holdDateTimeValue);
            }

            if (
              quote._inspectorId &&
              Array.isArray(quote._inspectorId) &&
              quote._inspectorId.length > 0
            ) {
              // Inspector might be populated or just IDs
              const inspector = quote._inspectorId[0];
              // Type guard for inspector object
              if (
                typeof inspector === 'object' &&
                inspector !== null &&
                '_id' in inspector
              ) {
                const inspectorObj = inspector as Record<string, any>;
                if (
                  inspectorObj._id &&
                  inspectorObj.firstName &&
                  inspectorObj.lastName
                ) {
                  setSelectedSlotInspectorInfo({
                    _id: String(inspectorObj._id),
                    firstName: String(inspectorObj.firstName),
                    lastName: String(inspectorObj.lastName),
                    thumb: inspectorObj.thumb ? String(inspectorObj.thumb) : '',
                  });
                }
              }
            }
          }

          // Navigation is now handled by useAutoNavigateToStep hook
          // which watches context state and navigates to the appropriate step
          // This ensures navigation happens after context is fully populated

          // Mark slug as loaded and reset flags
          setLoadedSlug(quote.slug);
          setShouldResumeDraft(false);
          // console.log('[DraftLoader] ✅ Hydration complete! Draft loaded:', quote.slug);
        } else if (
          quote &&
          quote.status !== 'draft' &&
          quote.status !== 'abandoned'
        ) {
          // Quote was completed or converted to pending/accepted/rejected - can't resume
          // console.log('[DraftLoader] ⚠️ Quote found but status is not resumable:', quote.status);
          clearDraftFromStorage();
          setDraftQuoteSlug(null);
          setDraftQuoteId(null);
          setShouldResumeDraft(false); // Clear resume flag to prevent stale navigation
        } else {
          // Quote not found or other issue
          console.log('[DraftLoader] ⚠️ Quote not found or invalid');
          clearDraftFromStorage();
          setDraftQuoteSlug(null);
          setDraftQuoteId(null);
          setShouldResumeDraft(false); // Clear resume flag to prevent stale navigation
        }
      } catch (error: any) {
        console.error('[DraftLoader] ❌ Error loading draft:', error);
        // Don't handle errors if component unmounted or request aborted
        if (!isMounted || abortControllerRef.current?.signal.aborted) {
          return;
        }

        // Clear invalid draft from storage on error
        clearDraftFromStorage();
        setDraftQuoteSlug(null);
        setDraftQuoteId(null);
        setShouldResumeDraft(false);

        // Note: We don't show user-facing error messages here because:
        // 1. User can manually navigate if needed
        // 2. Error is already logged for debugging
        // 3. Auto-navigation hook will handle navigation based on existing context
      } finally {
        isLoadingRef.current = false;
        if (isMounted) {
          setIsLoadingDraft(false);
        }
      }
    };

    loadDraft();

    // Cleanup function to prevent state updates on unmounted component
    // Cancel any pending requests when component unmounts or dependencies change
    return () => {
      isMounted = false; // Mark component as unmounted to skip state updates
      isLoadingRef.current = false;

      // Abort any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Dependencies: draftSlug, shouldLoad, loadedSlug, shouldResumeDraft
    // Run when slug changes OR shouldLoad flag changes OR explicit resume action
  }, [draftSlug, shouldLoad, loadedSlug, shouldResumeDraft]);

  return null; // This component doesn't render anything
}
